/** Capture before analytics starts, without loading the electrical engine. */
import { MAX_AGENT_DRAFT_HASH_CHARS } from "./handoff-limits";

let arrivalHash: string | null = null;
const SESSION_KEY = "crimp:agent-draft-arrival-v1";
const ARRIVAL_EVENT = "crimp:agent-draft-arrival";
let snapshot = { hash: null as string | null, revision: 0 };

export function isAgentDraftReviewPath(pathname: string): boolean {
  return /^\/review\/?$/.test(pathname);
}

function notifyArrival(): void {
  snapshot = { hash: arrivalHash, revision: snapshot.revision + 1 };
  window.dispatchEvent(new Event(ARRIVAL_EVENT));
}

export function captureAgentDraftArrival(): void {
  if (typeof window === "undefined") return;
  const hash = window.location.hash;
  const hasDraftHash = hash.startsWith("#agent-draft");
  // Even malformed/oversized payloads must disappear before any automatic URL
  // properties are captured. Only a bounded, tab-local recovery copy is kept.
  if (hasDraftHash) window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`);
  const params = new URLSearchParams(window.location.search);
  const plainCanvas = (/^\/app\/?$/.test(window.location.pathname) || isAgentDraftReviewPath(window.location.pathname)) &&
    !["p", "s", "pub", "new", "starter", "example", "component"].some(key => params.has(key));
  if (!plainCanvas) {
    clearAgentDraftArrival();
    return;
  }
  if (hasDraftHash) {
    arrivalHash = hash.length <= MAX_AGENT_DRAFT_HASH_CHARS ? hash : "#agent-draft=invalid";
    try { window.sessionStorage.setItem(SESSION_KEY, arrivalHash); } catch { /* Review still works in this page. */ }
    notifyArrival();
  } else {
    const previousHash = arrivalHash;
    try {
      const saved = window.sessionStorage.getItem(SESSION_KEY);
      arrivalHash = saved && saved.startsWith("#agent-draft=") && saved.length <= MAX_AGENT_DRAFT_HASH_CHARS ? saved : null;
      if (saved && !arrivalHash) window.sessionStorage.removeItem(SESSION_KEY);
    } catch {
      arrivalHash = null;
    }
    if (arrivalHash !== previousHash) notifyArrival();
  }
}

export function getAgentDraftArrivalHash(): string | null {
  return arrivalHash;
}

export function hasPendingAgentDraft(): boolean {
  return arrivalHash !== null;
}

export function getAgentDraftArrivalSnapshot(): { hash: string | null; revision: number } {
  return snapshot;
}

export function subscribeAgentDraftArrival(listener: () => void): () => void {
  window.addEventListener(ARRIVAL_EVENT, listener);
  return () => window.removeEventListener(ARRIVAL_EVENT, listener);
}

export function clearAgentDraftArrival(expectedRevision?: number): void {
  if (expectedRevision !== undefined && expectedRevision !== snapshot.revision) return;
  const changed = arrivalHash !== null;
  arrivalHash = null;
  try { window.sessionStorage.removeItem(SESSION_KEY); } catch { /* Storage may be blocked. */ }
  if (changed && typeof window !== "undefined") notifyArrival();
}
